import { NisModal } from "../lib/ui/NisModal";
import Widget from "../lib/ui/Widget";
import { ewent, observe } from "../lib/reactive";
import lodash from "lodash";
import { Alt1 } from "../lib/alt1/Alt1";
import { Rectangle, Vector2 } from "../lib/math";
import { GameMapMiniWidget, levelIcon } from "../lib/gamemap/GameMap";
import ButtonRow from "../lib/ui/ButtonRow";
import LightButton from "../cluetrainer/ui/widgets/LightButton";
import ImportStringModal from "../cluetrainer/ui/widgets/modals/ImportStringModal";
import ExportStringModal from "../cluetrainer/ui/widgets/modals/ExportStringModal";
import { MapEntity } from "../lib/gamemap/MapEntity";
import { TileCoordinates, TileRectangle } from "../lib/runescape/coordinates";
import * as leaflet from "leaflet";
import { GameLayer } from "../lib/gamemap/GameLayer";
import { CompassReader } from "../cluetrainer/cluesolving/cluereader/CompassReader";
import { Compasses } from "../cluetrainer/cluetheory/Compasses";
import { util } from "../lib/util/util";
import { clue_data } from "../data/clues";
import Properties from "../cluetrainer/ui/widgets/Properties";
import { C } from "../lib/ui/constructors";
import { Notification } from "../cluetrainer/ui/NotificationBar";
import { FullCompassCalibrationFunction } from "../cluetrainer/cluesolving/cluereader/capture/CompassCalibrationFunction";
import { Angles } from "../lib/math/Angles";
import { storage } from "../lib/util/storage";
import KeyValueStore from "../lib/util/KeyValueStore";
import { ConfirmationModal } from "../cluetrainer/ui/widgets/modals/ConfirmationModal";
import { direction, PathFinder } from "../lib/runescape/movement";
import { GameMapControl } from "../lib/gamemap/GameMapControl";
import TransportLayer from "../cluetrainer/ui/map/TransportLayer";
import { TileArea } from "../lib/runescape/coordinates/TileArea";
import { ChunkedData } from "../lib/util/ChunkedData";
import { SelectTileInteraction } from "../lib/gamemap/interaction/SelectTileInteraction";
import { InteractionGuard } from "../lib/gamemap/interaction/InteractionLayer";
import InteractionTopControl from "../cluetrainer/ui/map/InteractionTopControl";
import { PathGraphics } from "../cluetrainer/ui/path_graphics";
var getExpectedAngle = Compasses.getExpectedAngle;
var greatestCommonDivisor = util.greatestCommonDivisor;
var ANGLE_REFERENCE_VECTOR = Compasses.ANGLE_REFERENCE_VECTOR;
var cleanedJSON = util.cleanedJSON;
var italic = C.italic;
var notification = Notification.notification;
var hbox = C.hbox;
var vbox = C.vbox;
var angleDifference = Angles.angleDifference;
var avg = util.avg;
var gielinor_compass = clue_data.gielinor_compass;
var async_init = util.async_init;
var profileAsync = util.profileAsync;
var hgrid = C.hgrid;
var index = util.index;
import { HostedMapCollisionData, TileCollisionData } from "../lib/runescape/CollisionData";
import { LeafletUtils } from "../lib/gamemap/LeafletUtils";
import { LeafletPolygonConstructors } from "../lib/gamemap/LeafletPolygonConstructors";
import { FakeLodash } from "../lib/coreutil/FakeLodash";
export var Fraction;
(function (Fraction) {
    function value(rationale) {
        return rationale.y / rationale.x;
    }
    Fraction.value = value;
    function reduce(self) {
        const gcd = greatestCommonDivisor(Math.abs(self.x), Math.abs(self.y));
        return Vector2.scale(1 / gcd, self);
    }
    Fraction.reduce = reduce;
})(Fraction || (Fraction = {}));
var OffsetSelection;
(function (OffsetSelection) {
    function activeOffset(self) {
        return self?.highlighted_offset ?? self?.offset;
    }
    OffsetSelection.activeOffset = activeOffset;
})(OffsetSelection || (OffsetSelection = {}));
class SelectionStatusWidget extends Widget {
    constructor(status, tool) {
        super();
        this.tool = tool;
        const layout = new Properties().appendTo(this);
        layout.header("Selection Status");
        layout.named("Offset", `${Vector2.toString(status.offset.offset)}, (${Angles.toString(CalibrationTool.shouldAngle(status.offset.offset), 3)})`);
        if (status.offset.auto) {
            const delta = Angles.angleDifference(status.offset.auto.desired_angle, status.offset.auto.actual_angle);
            const range_size = Angles.AngleRange.size(status.offset.auto.desired_range);
            const is_far_away = delta > (range_size / 10);
            const text = `Δ=${Angles.radiansToDegrees(delta).toFixed(3)}° (${((delta / range_size) * 100).toFixed(2)}% of ${Angles.toString(Angles.AngleRange.size(status.offset.auto.desired_range), 3)} range)`;
            layout.named("Auto", is_far_away ? C.span(text).css("color", "red") : text);
            layout.row(new LightButton("Backlog").slim().onClick(() => {
                this.tool.spot_queue.backlog(status.offset);
            }));
        }
        else {
            if (this.tool.spot_queue.size() > 0) {
                layout.named("Auto", new LightButton("Resume")
                    .onClick(() => this.tool.spot_queue.dequeue()));
            }
            else {
                layout.named("Auto", "");
            }
        }
        layout.header("Existing Sample", "left", 1);
        if (status.existing_sample) {
            layout
                .named("Sampled", hgrid(Angles.toString(status.existing_sample.is_angle, 3), new LightButton("Delete")
                .slim()
                .setEnabled(!!status.existing_sample)
                .onClick(() => this.tool.delete())))
                .named("Fingerprint", CompassReader.ReadFingerprint.toString(status.existing_sample.fingerprint));
        }
        else {
            layout.row(italic("None"));
        }
        layout.row(new LightButton("Queue neighbouring samples").slim().onClick(() => {
            this.tool.spot_queue.push({
                name: `Neigbours of ${Vector2.toString(status.offset.offset)}`,
                function: () => {
                    return this.tool.sample_set.function.reverse_sample(CalibrationTool.shouldAngle(status.offset.offset)).relevant_samples.flatMap(s => s.raw_samples.map(s => s.position)).map(o => ({ offset: o }));
                }
            });
        }));
    }
}
class SampleSetBuilder {
    constructor(tool) {
        this.tool = tool;
        this.record_event = ewent();
        this.state = {
            samples: [],
            timestamp: Date.now(),
        };
        this.memory = KeyValueStore.instance().variable("preferences/calibrationtool/state");
        this.set_changed = ewent();
        this.set_loaded = ewent();
        this.update();
        this.memory.get().then(value => {
            if (value) {
                this.state = value;
                this.update();
            }
            this.set_loaded.trigger(this);
        });
        this.set_loaded.trigger(this);
    }
    date() {
        return new Date(this.state.timestamp);
    }
    changed() {
        this.state.timestamp = Date.now();
        this.memory.set(this.state);
        this.update();
    }
    update() {
        this.function = new FullCompassCalibrationFunction(FullCompassCalibrationFunction.combine(this.state.samples));
        this.set_changed.trigger(this.state.samples);
    }
    async set(sample) {
        const really = await ConfirmationModal.simple("Load Calibration Function", "Are you sure? This will irreversibly delete your collected samples.", "Cancel", "Confirm").do();
        if (!really)
            return false;
        this.state.samples = sample;
        this.changed();
        this.set_loaded.trigger(this);
        return true;
    }
    getCSV() {
        return util.makeCSV(this.state.samples)({ name: "Is", f: e => e.is_angle }, { name: "Should", f: e => CalibrationTool.shouldAngle(e.position) }, { name: "Delta", f: e => Angles.angleDifferenceSigned(e.is_angle, CalibrationTool.shouldAngle(e.position)) });
    }
    delete(...positions) {
        const old_length = this.state.samples.length;
        for (const position of positions) {
            const entry_index = this.state.samples.findIndex(s => Vector2.eq(s.position, position));
            if (entry_index >= 0) {
                const [e] = this.state.samples.splice(entry_index, 1);
                this.record_event.trigger(e);
            }
        }
        if (this.state.samples.length != old_length)
            this.changed();
    }
    sort() {
        this.state.samples = FakeLodash.sortBy(this.state.samples, s => CalibrationTool.shouldAngle(s.position));
    }
    record(offset, state) {
        const index_of_existing_sample = this.state.samples.findIndex(s => Vector2.eq(s.position, offset));
        if (index_of_existing_sample >= 0)
            this.state.samples.splice(index_of_existing_sample, 1);
        const new_sample = { position: offset, is_angle: state.result.raw_angle, fingerprint: state.result.fingerprint };
        this.state.samples.push(new_sample);
        this.sort();
        this.changed();
        this.record_event.trigger(new_sample);
    }
    find(offset) {
        if (!offset)
            return null;
        const should = CalibrationTool.shouldAngle(offset);
        let low = 0;
        let high = this.state.samples.length;
        while (true) {
            if (low == high)
                return null;
            const median = ~~((low + high) / 2);
            const median_should = CalibrationTool.shouldAngle(this.state.samples[median].position);
            if (should == median_should)
                return this.state.samples[median];
            else if (should > median_should)
                low = median + 1;
            else
                high = median;
        }
        //return this.state.samples.find(s => Vector2.eq(s.position, offset))
    }
    size() {
        return this.state.samples.length;
    }
    get() {
        return this.state.samples;
    }
}
function indexOfMinBy(data, f) {
    let index = undefined;
    let min_Value = Number.MAX_VALUE;
    for (let i = 0; i < data.length; i++) {
        const v = data[i];
        const x = f(v, i);
        if (x != undefined && x < min_Value) {
            index = i;
            min_Value = x;
        }
    }
    return index;
}
class TravellingSalesmanProblem {
    constructor(spots, distance, start_position) {
        this.spots = spots;
        this.distance = distance;
        this.start_position = start_position;
        this.come_from_solution = this.spots.map((_, i) => i);
        this.spots_with_start = [...spots, start_position];
        this.start_position_index = spots.length;
        this.distance_map = this.spots_with_start.map(_ => this.spots_with_start.map((_) => undefined));
        this.distance_map.forEach((row, i) => row[i] = 0);
        this._init = async_init(async () => await profileAsync(async () => {
            for (let i = 0; i < this.distance_map.length; i++) {
                for (let j = i + 1; j < this.distance_map.length; j++) {
                    const a = this.spots_with_start[i];
                    const b = this.spots_with_start[j];
                    const d = !a || !b
                        ? 0
                        : await distance.distance(a, b);
                    this.distance_map[i][j] = d;
                    this.distance_map[j][i] = d;
                }
            }
        }, `Computing distances ${this.spots.length}`));
    }
    async wait(f) {
        return this._init.wait().then(() => f(this));
    }
    _assert_init() {
        if (!this._init.isInitialized())
            throw new Error("TravellingSalesmanProblem must be awaited before it can be solved.");
    }
    getDistance(a, b) {
        return this.distance_map[a][b];
    }
    _greedy() {
        this._assert_init();
        if (this.spots.length == 0)
            return [];
        // A solution is an array that maps spot indices to the index of the previous node in the path (come-from)
        const come_from_map = Array(this.spots.length).fill(undefined);
        let position = this.start_position_index;
        for (let i = 0; i < come_from_map.length; i++) {
            const greedy_best_i = indexOfMinBy(come_from_map, (value, i) => value == undefined ? this.getDistance(position, i) : undefined);
            come_from_map[greedy_best_i] = position;
            position = greedy_best_i;
        }
        return come_from_map;
    }
    greedy() {
        this.come_from_solution = util.profile(() => this._greedy(), `Greedy Salesman ${this.spots.length}`);
        return this;
    }
    getResult() {
        console.log(`Final cost ${this._evaluateComeFrom(this.come_from_solution)}`);
        return TravellingSalesmanProblem.go_to_to_solution(TravellingSalesmanProblem.come_from_to_go_to(this.come_from_solution)).map(p => this.spots[p]);
    }
    _evaluateComeFrom(come_from_solution) {
        return FakeLodash.sum(come_from_solution.map((come_from, pos) => this.getDistance(come_from, pos)));
    }
    _optimize_by_pairwise_exchange() {
        let best = TravellingSalesmanProblem.go_to_to_solution(TravellingSalesmanProblem.come_from_to_go_to(this.come_from_solution));
        let n = 0;
        while (n < 100) {
            let best_swap = undefined;
            let best_improvement = 0;
            for (let i = 0; i < this.spots.length; i++) {
                const a = i > 0 ? best[i - 1] : this.start_position_index;
                const b = best[i];
                const c = best[i + 1];
                for (let j = i + 1; j < this.spots.length; j++) {
                    const x = best[j - 1];
                    const y = best[j];
                    const z = j + 1 < best.length ? best[j + 1] : undefined;
                    let pre_swap = 0;
                    let post_swap = 0;
                    pre_swap += this.getDistance(a, b);
                    pre_swap += this.getDistance(b, c);
                    post_swap += this.getDistance(a, y);
                    if (j == i + 1) {
                        post_swap += this.getDistance(y, b);
                    }
                    else {
                        pre_swap += this.getDistance(x, y);
                        post_swap += this.getDistance(y, c);
                        post_swap += this.getDistance(x, b);
                    }
                    if (z != undefined) {
                        pre_swap += this.getDistance(y, z);
                        post_swap += this.getDistance(b, z);
                    }
                    const improvement = pre_swap - post_swap;
                    if (improvement > best_improvement) {
                        best_swap = [i, j];
                        best_improvement = improvement;
                    }
                }
            }
            if (!best_swap)
                break;
            n++;
            if (n > 100)
                throw new Error(`Aborting after ${n} swaps`);
            const copy = [...best];
            copy[best_swap[0]] = best[best_swap[1]];
            copy[best_swap[1]] = best[best_swap[0]];
            best = copy;
        }
        this.come_from_solution = TravellingSalesmanProblem.solution_to_come_from(best);
        console.log(`Did ${n} improvements`);
        return best;
    }
    optimize_by_pairwise_exchange() {
        util.profile(() => this._optimize_by_pairwise_exchange(), `Pairwise Optimization`);
        return this;
    }
}
(function (TravellingSalesmanProblem) {
    function come_from_to_go_to(come_from) {
        const go_to_solution = new Array(come_from.length + 1);
        come_from.forEach((come_from, go_to) => go_to_solution[come_from] = go_to);
        return go_to_solution;
    }
    TravellingSalesmanProblem.come_from_to_go_to = come_from_to_go_to;
    function solution_to_come_from(solution) {
        const come_from = [...solution];
        solution.forEach((s, i) => {
            come_from[s] = i == 0 ? solution.length : solution[i - 1];
        });
        return come_from;
    }
    TravellingSalesmanProblem.solution_to_come_from = solution_to_come_from;
    function go_to_to_solution(go_to) {
        const solution = [];
        let position = go_to[go_to.length - 1];
        while (position != undefined) {
            solution.push(position);
            position = go_to[position];
        }
        return solution;
    }
    TravellingSalesmanProblem.go_to_to_solution = go_to_to_solution;
    class StatefulDistanceFunction {
        comap(f) {
            const self = this;
            return new class extends StatefulDistanceFunction {
                distance(a, b) {
                    return self.distance(f(a), f(b));
                }
            };
        }
    }
    TravellingSalesmanProblem.StatefulDistanceFunction = StatefulDistanceFunction;
    class PathFindingDistanceFunction extends StatefulDistanceFunction {
        constructor() {
            super(...arguments);
            this.states = [];
        }
        async distance(a, b) {
            let state = this.states.find(s => TileCoordinates.eq(a, s.origin));
            if (!state) {
                this.states.push(state = {
                    origin: a,
                    state: PathFinder.init_djikstra(a)
                });
            }
            const path = await PathFinder.find(state.state, TileArea.init(b));
            return path ? PathFinder.pathLength(path) : Number.MAX_VALUE;
        }
    }
    TravellingSalesmanProblem.PathFindingDistanceFunction = PathFindingDistanceFunction;
    class AStarDistanceFunction extends StatefulDistanceFunction {
        constructor(map_date, max_distance) {
            super();
            this.map_date = map_date;
            this.max_distance = max_distance;
        }
        async distance(a, b) {
            if (TileCoordinates.eq(a, b))
                return 0;
            if (Vector2.max_axis(Vector2.sub(a, b)) > this.max_distance)
                return Math.min(2 * this.max_distance, Vector2.max_axis(Vector2.sub(a, b)));
            class Queue {
                constructor(max) {
                    this.max = max;
                    this.row_index = 0;
                    this.element_index = 0;
                    this.size = 0;
                    this.data = new Array(max + 1).fill(null).map(() => []);
                }
                push(node) {
                    if (node.estimated_total <= this.max) {
                        this.data[node.estimated_total].push(node);
                        this.size++;
                    }
                }
                pop() {
                    while (this.element_index >= this.data[this.row_index].length && this.row_index < this.max) {
                        this.row_index++;
                        this.element_index = 0;
                    }
                    if (this.row_index > this.max)
                        return null;
                    this.size--;
                    return this.data[this.row_index][this.element_index++];
                }
            }
            function estimate(c) {
                return Vector2.max_axis(Vector2.sub(b, c));
            }
            const queue = new Queue(this.max_distance);
            const grid = new ChunkedData();
            function push(node) {
                if (node.estimated_total <= queue.max && grid.get(node.position) == undefined) {
                    grid.set(node.position, node.distance);
                    queue.push(node);
                }
            }
            push({
                position: ChunkedData.split(a),
                distance: 0,
                estimated_total: estimate(a)
            });
            let iterations = 1000;
            while (iterations > 0) {
                iterations--;
                const next = queue.pop();
                if (!next)
                    break;
                const tile_data = await this.map_date.getTile(next.position.coords);
                for (let dir of direction.all) {
                    if (TileCollisionData.isFree(tile_data, dir)) {
                        const target_tile = TileCoordinates.move(next.position.coords, direction.toVector(dir));
                        if (TileCoordinates.equals(b, target_tile))
                            return next.distance + 1;
                        const successor = {
                            position: ChunkedData.split(target_tile),
                            distance: next.distance + 1,
                            estimated_total: next.distance + 1 + estimate(target_tile)
                        };
                        push(successor);
                    }
                }
            }
            return Math.min(2 * this.max_distance, Vector2.max_axis(Vector2.sub(a, b)));
        }
    }
    TravellingSalesmanProblem.AStarDistanceFunction = AStarDistanceFunction;
})(TravellingSalesmanProblem || (TravellingSalesmanProblem = {}));
/**
 * Uses the farey sequence to find the rationale number (as a Vector2 where y is the numerator and x is the denominator) closest to the given number,
 * where the denominator is not bigger than the given limit.
 *
 * @param max_denominator The maximum denominator.
 * @param target_fraction The fraction to approximate
 * @param close_enough
 */
function approximateFractionAsRationaleNumber(max_denominator, target_fraction, close_enough = _ => false) {
    function approximateAsRationaleNumberImplementation(target_fraction, close_enough) {
        const target_number = Fraction.value(target_fraction);
        let lower = { y: 0, x: 1 };
        let higher = { y: 1, x: 1 };
        if (close_enough(lower))
            return lower;
        if (close_enough(higher))
            return higher;
        while (true) {
            const mediant = Vector2.add(lower, higher); // interestingly, c is already in reduced form
            if (close_enough(mediant))
                return mediant;
            const mediant_value = Fraction.value(mediant);
            // Because lower and higher are always (reduced) neighbours in a farey sequence, their mediant will also be reduced.
            // if the denominator is too big, return the closest of lower or higher
            if (mediant.x > max_denominator) {
                return FakeLodash.minBy([lower, higher], frac => Math.abs(target_number - Fraction.value(frac)));
            }
            // adjust the interval:
            if (mediant_value < target_number)
                lower = mediant;
            else
                higher = mediant;
        }
    }
    const signs = Vector2.sign(target_fraction);
    const must_be_swapped = Math.abs(target_fraction.y) > Math.abs(target_fraction.x);
    const forth = must_be_swapped ? v => Vector2.swap(Vector2.mul(signs, v)) : v => Vector2.mul(signs, v);
    const back = must_be_swapped ? v => Vector2.mul(signs, Vector2.swap(v)) : v => Vector2.mul(signs, v);
    return back(approximateAsRationaleNumberImplementation(forth(target_fraction), v => close_enough(back(v))));
}
class CalibrationQueue {
    head() {
        return this._backlog[this._backlog.length - 1];
    }
    pop() {
        const popped = this._backlog.pop();
        if (popped)
            this.changed.trigger(this);
        return popped;
    }
    constructor(parent) {
        this.parent = parent;
        this._backlog = [];
        this.changed = ewent();
        this.parent.sample_set.record_event.on(async (sample) => {
            const head = this.head();
            const head_removal_index = this.remove(sample.position);
            while (this.head()?.queue.length == 0) {
                const head = this.pop();
                if (head.filler?.repeatable)
                    if (await this.push(head.filler))
                        return;
            }
            this.dequeue(head == this.head() ? head_removal_index : 0);
        });
        this.parent.reference.subscribe(async (ref) => {
            CalibrationQueue.State.sort(this.head(), ref);
            this.changed.trigger(this);
        });
    }
    backlog(offset) {
        const head = this.head();
        if (!head)
            return;
        const index = head.queue.indexOf(offset);
        if (index >= 0) {
            head.queue.push(...head.queue.splice(index, 1));
            this.changed.trigger(this);
            this.dequeue();
        }
    }
    dequeue(index = 0) {
        const head = this.head();
        if (!head)
            return;
        if (index >= 0 && index < head.queue.length) {
            this.parent.setOffset(head.queue[index]);
            return true;
        }
        return false;
    }
    remove(offset) {
        const index_removed_from_current_state = index(this._backlog.map(stack => CalibrationQueue.State.remove(stack, offset)), -1);
        if (index_removed_from_current_state != undefined)
            this.changed.trigger(this);
        return index_removed_from_current_state;
    }
    size() {
        return this.head()?.queue?.length ?? 0;
    }
    clear() {
        if (this._backlog.length > 0) {
            this._backlog = [];
            this.changed.trigger(this);
        }
    }
    async push(f) {
        const new_state = {
            queue: f.function(),
            filler: f
        };
        if (new_state.queue.length == 0)
            return false;
        if (!f.disable_sorting)
            await CalibrationQueue.State.sort(new_state, this.parent.reference.value(), OffsetSelection.activeOffset(this.parent.selection.value().offset));
        this._backlog.push(new_state);
        this.dequeue();
        this.changed.trigger(this);
        return true;
    }
}
(function (CalibrationQueue) {
    let State;
    (function (State) {
        function remove(self, offset) {
            const i = self.queue.findIndex(e => Vector2.eq(offset, e.offset));
            if (i >= 0) {
                self.queue.splice(i, 1);
                return i;
            }
            return undefined;
        }
        State.remove = remove;
        async function sort(self, reference, start_offset = undefined) {
            if (self.queue.length == 0)
                return;
            const rect = { "topleft": { "x": 1950, "y": 4152 }, "botright": { "x": 3898, "y": 2594 }, "level": 0 };
            const backlog = [];
            async function findAccessibleOffset(sel, i = 1) {
                const off = Vector2.scale(i, sel.offset);
                const pos = TileCoordinates.move(reference, off);
                if (!TileRectangle.contains(rect, pos))
                    return 0;
                if (await HostedMapCollisionData.get().isAccessible(pos))
                    return i;
                else
                    return findAccessibleOffset(sel, i + 1);
            }
            for (let offsetSelection of self.queue) {
                const index = await findAccessibleOffset(offsetSelection);
                if (index == 0) {
                    backlog.push(offsetSelection);
                }
                else {
                    offsetSelection.highlighted_offset = Vector2.scale(index, offsetSelection.offset);
                }
            }
            backlog.forEach(offsetSelection => self.queue.splice(self.queue.indexOf(offsetSelection), 1));
            const sorted_by_should_angle = FakeLodash.sortBy(self.queue, s => CalibrationTool.shouldAngle(s.offset));
            self.queue = await new TravellingSalesmanProblem(sorted_by_should_angle, 
            //new TravellingSalesmanProblem.PathFindingDistanceFunction()
            new TravellingSalesmanProblem.AStarDistanceFunction(HostedMapCollisionData.get(), 64)
                .comap(s => TileCoordinates.move(reference, OffsetSelection.activeOffset(s))), start_offset ? { offset: start_offset } : null)
                .wait(problem => problem
                .greedy()
                .optimize_by_pairwise_exchange()
                .getResult());
            self.queue.push(...FakeLodash.sortBy(backlog, s => CalibrationTool.shouldAngle(s.offset)));
        }
        State.sort = sort;
    })(State = CalibrationQueue.State || (CalibrationQueue.State = {}));
})(CalibrationQueue || (CalibrationQueue = {}));
class CalibrationQueueView extends Widget {
    constructor(queue) {
        super();
        this.queue = queue;
        this.init(this.props = new Properties());
        queue.changed.on(() => this.render());
        this.render();
    }
    render() {
        this.props.empty();
        this.props.header("Offset Queue");
        const head = this.queue.head();
        const head_size = head?.queue?.length ?? 0;
        this.props.named("Type", head?.filler ? head.filler.name : "");
        this.props.named("Size", hgrid(head_size.toString(), new LightButton("Pop").setEnabled(!!this.queue.head()).slim().onClick(() => this.queue.pop())));
        this.props.named("Backlog", hgrid((FakeLodash.sumBy(this.queue._backlog, l => l.queue.length) - head_size).toString(), new LightButton("Clear").slim().onClick(() => this.queue.clear())));
    }
}
function findAutoSpotForAngleRange(range) {
    const angle = Angles.AngleRange.mean(range);
    const range_size = Angles.AngleRange.size(range);
    const offset = approximateFractionAsRationaleNumber(1000, { y: -Math.sin(angle), x: -Math.cos(angle) }, offset => angleDifference(CalibrationTool.shouldAngle(offset), angle) < (range_size / 20));
    return {
        offset: offset,
        auto: {
            desired_angle: angle,
            desired_range: range,
            actual_angle: CalibrationTool.shouldAngle(offset),
        }
    };
}
export class CompassCalibrationTool extends NisModal {
    constructor() {
        super({
            size: "fullscreen",
            fixed: true,
            disable_close_button: false
        });
        this.reference_memory = new storage.Variable("preferences/calibrationtool/reference", () => gielinor_compass.spots[0]);
        this.selection = observe(null);
        this.reference = observe(this.reference_memory.get());
        this.sample_set = new SampleSetBuilder(this);
        this.spot_queue = new CalibrationQueue(this);
        this.setOffset({ offset: { x: -1, y: 0 } });
        this.handler = Alt1.instance().main_hotkey.subscribe(0, (e) => {
            this.commit();
        });
        this.hidden.on(() => {
            this.reader.stop();
            this.handler.remove();
        });
        this.reader = new CompassReader.Service(null, { warn_antialiasing: true }, false, null, true).start();
        this.sample_set.set_changed.on(() => {
            this.updateCurrentPlausibility();
            this.updateFunctionStatusView();
            this.setOffset(this.selection.value().offset);
        });
        this.sample_set.set_loaded.on(() => {
            this.spot_queue.clear();
        });
        this.reader.onChange(() => {
            this.updateCurrentPlausibility();
        });
        this.reference.subscribe(ref => {
            this.reference_memory.set(ref);
        });
        this.selection.subscribe(v => {
            this.updateSelectionView();
            this.updateCurrentPlausibility();
        });
    }
    updateCurrentPlausibility() {
        const props = new Properties();
        props.header("Current Read Plausibility");
        const reader_state = this.reader.state();
        if (reader_state?.state != "normal") {
            props.row("No angle detected");
        }
        else {
            const result = this.sample_set.function.sample(reader_state.result.raw_angle);
            props.named("Raw Read", Angles.toString(reader_state.result.raw_angle, 3));
            props.named("Fingerprint", CompassReader.ReadFingerprint.toString(reader_state.result.fingerprint));
            props.named("Calibrated", Angles.AngleRange.toString(result.result.range, 3) + " (" + Angles.UncertainAngle.toString(result.result, 3) + ")");
            props.named("Sample", result.details.type);
            switch (result.details.type) {
                case "outside":
                    props.named("Before", FullCompassCalibrationFunction.CombinedSample.toString(result.details.before));
                    props.named("Hit", "-");
                    props.named("After", FullCompassCalibrationFunction.CombinedSample.toString(result.details.after));
                    break;
                case "within":
                    props.named("Before", FullCompassCalibrationFunction.CombinedSample.toString(result.details.before));
                    props.named("Hit", FullCompassCalibrationFunction.CombinedSample.toString(result.details.in_sample));
                    props.named("After", FullCompassCalibrationFunction.CombinedSample.toString(result.details.after));
                    break;
            }
            const current_sample = this.sample_set.function.sample(reader_state.result.raw_angle);
            const isPlausible = Angles.UncertainAngle.contains(current_sample.result, CalibrationTool.shouldAngle(this.selection.value().offset.offset));
            props.named("Plausible", hgrid(isPlausible ? "Yes" : "No", new LightButton(isPlausible ? "Commit (Alt+1)" : "Force Commit")
                .slim()
                .onClick(async () => {
                if (!isPlausible) {
                    const really = await ConfirmationModal.simple("Commit Implausible Sample?", "Are you sure you want to record this implausible sample?", "Cancel", "Confirm").do();
                    if (!really)
                        return;
                }
                this.commit(true);
            })));
        }
        this.current_calibrated_angle_view.empty().append(props);
    }
    updateFunctionStatusView() {
        const props = new Properties();
        props.header("Function Status");
        const uncalibrated = this.sample_set.function.uncalibrated_ranges();
        Angles.AngleRange.size(uncalibrated[0]);
        const minEpsilons = this.sample_set.function.minEpsilons();
        const bad_samples = this.sample_set.function.bad_samples();
        props.named("Last Change", this.sample_set.date().toLocaleString());
        props.named("Samples", this.sample_set.size().toString());
        props.named("Unique Angles", hgrid(this.sample_set.function.samples.length.toString(), new LightButton("Analyze").slim().onClick(() => {
            this.layer.guard.set(new SelectTileInteraction()
                .add(new InteractionTopControl()
                .setText("Select a tile to analyze."))
                .onCommit(tile => {
                const grouped = lodash.groupBy(gielinor_compass.spots, spot => this.sample_set.function.reverse_sample(Compasses.getExpectedAngle(tile, spot)).angle.median);
                const discriminated_spots = Object.entries(grouped).filter(([angle, spots]) => {
                    return spots.length == 1;
                });
                console.log(Object.fromEntries(Object.entries(grouped).filter(([key, value]) => value.length > 1).map(([key, value]) => [key, Angles.UncertainAngle.fromRange(Angles.AngleRange.fromAngles(...value.map(spot => Compasses.getExpectedAngle(tile, spot))))])));
                console.log(`Discriminates ${discriminated_spots.length}/${gielinor_compass.spots.length} spots `);
            }));
        })));
        props.named("Average Epsilon", Angles.toString(this.sample_set.function.averageEpsilon(), 3));
        props.named("Min Epsilon (Min)", `${Angles.toString(Math.min(...minEpsilons), 3)} - ${Angles.toString(Math.max(...minEpsilons), 3)} (${Angles.toString(avg(...minEpsilons), 3)} avg.)`);
        props.named("Implausibilities", hgrid(bad_samples.length.toString(), new LightButton("Queue All")
            .slim()
            .setEnabled(bad_samples.length > 0)
            .onClick(() => {
            this.spot_queue.push({
                name: "Implausibilities",
                function: () => bad_samples.flatMap(s => s.raw_samples).map(s => ({
                    offset: s.position
                }))
            });
        })));
        /*props.row(new LightButton("Delete all near 90°").onClick(() => {
          this.sample_set.delete(...this.sample_set.get().filter(s => {
            const should_angle = CalibrationTool.shouldAngle(s.position)
    
            for (let i = 0; i < 2; i++) {
              const diff = Angles.angleDifference(should_angle, i * Math.PI)
    
              if (diff < Angles.degreesToRadians(18) && diff > Angles.degreesToRadians(10)) return true
            }
    
            return false
          }).map(s => s.position))
        }))
    
        props.row(new LightButton("Queue Manual").onClick(() => {
            this.spot_queue.push({
              name: "Fill",
              show_line: true,
              disable_sorting: true,
              function: () => {
                type ProblematicSpots = {
                  reference: TileCoordinates,
                  spots: number[]
                }
    
                const problems: ProblematicSpots[] = [
                  {reference: {x: 2728, y: 3348, level: 0}, spots: [68, 388, 192, 100, 196, 390, 104, 158, 195]}, // legends cape
                  {reference: {x: 3005, y: 3375, level: 0}, spots: [352, 57, 317, 91, 158, 86]}, // natures amulet
                ]
    
                return problems.flatMap(problem => problem.spots.map(spot_id => {
                  return {
                    offset: Fraction.reduce(Vector2.sub(problem.reference, gielinor_compass.spots[spot_id - 1]))
                  }
                }))
              }
            })
          })
        )*/
        props.named("Largest Gap", hgrid(Angles.toString(Angles.AngleRange.size(uncalibrated[0]), 3), new LightButton("Queue").slim().onClick(() => this.fillQueueWithBiggestUncalibratedRange())));
        this.sample_set.function.uncalibrated_ranges();
        this.function_status_view.empty().append(props);
    }
    delete() {
        this.sample_set.delete(this.selection.value().offset.offset);
        this.selection.update(s => s.existing_sample = null);
    }
    commit(skip_plausibility_check = false) {
        const state = this.reader.state();
        if (!state || state.state != "normal") {
            notification("No angle  detected", "error").show();
            return;
        }
        if (!skip_plausibility_check) {
            const current_sample = this.sample_set.function.sample(state.result.raw_angle);
            const is_plausible = Angles.UncertainAngle.contains(current_sample.result, CalibrationTool.shouldAngle(this.selection.value().offset.offset));
            if (!is_plausible) {
                notification("Implausible angle. Click `Force Commit` to commit it anyway.", "error").show();
                return;
            }
        }
        if (state.state == "normal") {
            this.sample_set.record(this.selection.value().offset.offset, state);
        }
    }
    setOffset(offset, zoom = true) {
        if (!offset.auto) {
            const in_queue = this.spot_queue.head()?.queue?.find(e => Vector2.eq(e.offset, offset.offset));
            if (in_queue) {
                in_queue.highlighted_offset = offset.highlighted_offset;
                offset = in_queue;
            }
        }
        this.selection.set({
            existing_sample: this.sample_set.find(offset.offset),
            offset: offset,
        });
        if (this.layer) {
            if (zoom)
                this.layer.getMap().fitView(TileRectangle.from(TileCoordinates.move(this.reference.value(), OffsetSelection.activeOffset(offset))), {
                    maxZoom: this.layer.getMap().getZoom(),
                    animate: true
                });
        }
    }
    fillQueueWithBiggestUncalibratedRange() {
        const MAX_RANGE = Angles.degreesToRadians(0.3);
        this.spot_queue.push({
            name: "Largest Gap",
            repeatable: true,
            function: () => {
                const candidates = this.sample_set.function.uncalibrated_ranges();
                const largest_range = Angles.AngleRange.size(candidates[0]);
                const index_of_first_range_not_considered = Math.min(300, candidates.findIndex((candidate, index) => {
                    const size = Angles.AngleRange.size(candidate);
                    return size < 0.7 * largest_range && size < MAX_RANGE;
                }));
                const taken_ranges = (index_of_first_range_not_considered >= 0
                    ? candidates.slice(0, index_of_first_range_not_considered)
                    : candidates).flatMap(range => {
                    const size = Angles.AngleRange.size(range);
                    const sections = Math.max(1, Math.ceil(size / MAX_RANGE));
                    return Angles.AngleRange.split(range, sections);
                });
                return taken_ranges.map(range => findAutoSpotForAngleRange(range));
            }
        });
        return;
    }
    render() {
        super.render();
        this.title.set("Compass Calibration");
        this.body.css("display", "flex");
        const menu_column = vbox().css("min-width", "300px").appendTo(this.body);
        c().css("min-width", "20px").css("max-width", "20px").appendTo(this.body);
        const map = new GameMapMiniWidget()
            .css2({
            "width": "100%",
        })
            .appendTo(this.body);
        setTimeout(() => map.map.invalidateSize(), 1000);
        new ButtonRow().buttons(new LightButton("Export JSON").onClick(() => {
            new ExportStringModal(CalibrationTool.cleanExport(this.sample_set.get())).show();
        }), new LightButton("Export CSV").onClick(() => {
            new ExportStringModal(this.sample_set.getCSV()).show();
        }), new LightButton("Export Small").onClick(() => {
            new ExportStringModal("[" + this.sample_set.function.samples.map(s => {
                return `[${s.is_angle.from.toFixed(4)},${s.is_angle.to.toFixed(4)},${s.should_angle.from.toFixed(4)},${s.should_angle.to.toFixed(4)}]`;
            }).join(",") + "]", "", "calibration_samples.json").show();
        })).appendTo(menu_column);
        new ButtonRow().buttons(new LightButton("Import JSON").onClick(async () => {
            const imp = (await new ImportStringModal(input => {
                return JSON.parse(input);
            }).do()).imported;
            if (imp) {
                this.sample_set.set(imp).then(did_set => {
                    if (did_set)
                        this.fillQueueWithBiggestUncalibratedRange();
                });
            }
        }), new LightButton("Load NO AA").onClick(async () => {
            const response = await fetch("/data/compass/no_aa_calibration.json");
            this.sample_set.set(await response.json());
        }), new LightButton("Load MSAA").onClick(() => {
            this.sample_set.set(FakeLodash.cloneDeep(CompassReader.msaa_samples));
        }), new LightButton("Reset").onClick(() => {
            this.sample_set.set([]);
        })).appendTo(menu_column);
        this.selection_status_view = c().appendTo(menu_column);
        this.function_status_view = hbox().appendTo(menu_column);
        this.current_calibrated_angle_view = hbox().appendTo(menu_column);
        this.calibration_queue_view = new CalibrationQueueView(this.spot_queue).appendTo(menu_column);
        this.hovered_tile_reverse_sample_view = hbox().appendTo(menu_column);
        this.updateFunctionStatusView();
        this.updateSelectionView();
        this.updateCurrentPlausibility();
        this.updateHoveredTileView(null);
        map.map.addGameLayer(this.layer = new CalibrationTool.Layer(this));
        this.layer.centerOnReference();
    }
    updateSelectionView() {
        if (!this.selection_status_view)
            return;
        new SelectionStatusWidget(this.selection.value(), this).appendTo(this.selection_status_view.empty());
    }
    updateHoveredTileView(tile) {
        const offset = tile ? Fraction.reduce(Vector2.sub(tile, this.reference.value())) : null;
        const existing_sample = offset ? this.sample_set.find(offset) : null;
        const layout = new Properties();
        layout.header("Hovered Tile");
        layout.named("Offset", offset ? `${Vector2.toString(offset)}, (${Angles.toString(CalibrationTool.shouldAngle(offset), 3)})` : "-");
        layout.header("Existing Sample", "left", 1);
        if (existing_sample) {
            layout
                .named("Sampled", Angles.toString(existing_sample.is_angle, 3))
                .named("Fingerprint", CompassReader.ReadFingerprint.toString(existing_sample.fingerprint));
        }
        else {
            layout.row(italic("None"));
        }
        this.hovered_tile_reverse_sample_view?.empty().append(layout);
    }
}
export var CalibrationTool;
(function (CalibrationTool) {
    var gielinor_compass = clue_data.gielinor_compass;
    var arrow = PathGraphics.arrow;
    var tilePolygon = LeafletPolygonConstructors.tilePolygon;
    function cleanExport(samples) {
        return "[\n" +
            FakeLodash.sortBy(samples, s => Vector2.angle(ANGLE_REFERENCE_VECTOR, { x: -s.position.x, y: -s.position.y })).map(s => cleanedJSON(s, undefined)).join(",\n")
            + "\n]";
    }
    CalibrationTool.cleanExport = cleanExport;
    function shouldAngle(offset) {
        return getExpectedAngle(offset, { x: 0, y: 0 });
    }
    CalibrationTool.shouldAngle = shouldAngle;
    class KnownMarker extends MapEntity {
        constructor(spot) {
            super();
            this.spot = spot;
            this.active = false;
            this.setInteractive();
        }
        setActive(v) {
            if (v != this.active) {
                this.active = v;
                this.requestRendering();
            }
            return this;
        }
        bounds() {
            return Rectangle.from(this.spot);
        }
        async render_implementation(props) {
            const opacity = this.active ? 1 : 0.5;
            const scale = (this.active ? 1 : 0.5) * (props.highlight ? 1.5 : 1);
            const marker = leaflet.marker(LeafletUtils.latLongFromVector2(this.spot), {
                icon: levelIcon(this.spot.level, scale),
                opacity: opacity,
                interactive: true,
                bubblingMouseEvents: true,
            }).addTo(this);
            return marker.getElement();
        }
    }
    CalibrationTool.KnownMarker = KnownMarker;
    class Layer extends GameLayer {
        constructor(tool) {
            super();
            this.tool = tool;
            this.sample_set_overlay = null;
            this.selection_overlay = null;
            this.queue_view = null;
            this.guard = new InteractionGuard().setDefaultLayer(this);
            this.add(new HoverTileDisplay(tool));
            this.add(new TransportLayer(true, { teleport_policy: "target_only", transport_policy: "none" }));
            this.markers = gielinor_compass.spots.map(spot => new KnownMarker(spot).addTo(this));
            this.tool.sample_set.set_changed.on(() => {
                this.updateSampleSetOverlay();
            });
            this.tool.spot_queue.changed.on(() => {
                this.updateQueueView();
            });
            this.tool.reference.subscribe(reference => {
                this.centerOnReference();
                this.markers.forEach(marker => {
                    marker.setActive(TileCoordinates.equals(marker.spot, reference));
                });
                this.updateSelectionOverlay();
                this.updateSampleSetOverlay();
                this.updateQueueView();
            }, true);
            this.tool.selection.subscribe(() => {
                this.updateSelectionOverlay();
                this.updateQueueView();
            });
        }
        centerOnReference() {
            const map = this.getMap();
            if (map) {
                map.fitView(TileRectangle.from(this.tool.reference.value()), {
                    maxZoom: 4
                });
            }
        }
        eventClick(event) {
            event.onPost(() => {
                if (event.active_entity instanceof KnownMarker) {
                    this.tool.reference.set(event.active_entity.spot);
                }
                else {
                    const off = Vector2.sub(event.tile(), this.tool.reference.value());
                    if (off.x == 0 && off.y == 0)
                        return;
                    const gcd = greatestCommonDivisor(Math.abs(off.x), Math.abs(off.y));
                    this.tool.setOffset({ offset: Vector2.scale(1 / gcd, off), highlighted_offset: off }, false);
                }
            });
        }
        updateSampleSetOverlay() {
            if (this.sample_set_overlay) {
                this.sample_set_overlay.remove();
                this.sample_set_overlay = null;
            }
            this.sample_set_overlay = leaflet.featureGroup().addTo(this);
            this.tool.sample_set.get().forEach((sample, i) => {
                const polygon = tilePolygon(Vector2.add(this.tool.reference.value(), sample.position)).setStyle({
                    color: "#06ffea",
                    fillOpacity: 0.2,
                    stroke: false
                }).addTo(this.sample_set_overlay);
            });
        }
        updateSelectionOverlay() {
            if (this.selection_overlay) {
                this.selection_overlay.remove();
                this.selection_overlay = null;
            }
            const selection = this.tool.selection.value();
            if (!selection || !this.tool.reference.value())
                return;
            this.selection_overlay = leaflet.featureGroup().addTo(this);
            for (let i = 1; i <= 100; i++) {
                const polygon = tilePolygon(Vector2.add(this.tool.reference.value(), Vector2.scale(i, selection.offset.offset))).addTo(this.selection_overlay);
                if (selection.existing_sample) {
                    polygon.setStyle({
                        color: "orange"
                    });
                }
            }
        }
        updateQueueView() {
            if (this.queue_view) {
                this.queue_view.remove();
                this.queue_view = null;
            }
            if (this.tool.spot_queue.size() == 0)
                return;
            this.queue_view = leaflet.featureGroup().addTo(this);
            const selection = this.tool.selection.value();
            const queue = this.tool.spot_queue.parent.spot_queue.head();
            queue.queue.forEach(sel => {
                if (!Vector2.eq(selection.offset.offset, sel.offset)) {
                    tilePolygon(TileCoordinates.move(this.tool.reference.value(), OffsetSelection.activeOffset(sel)))
                        .setStyle({ color: "#ffff00" })
                        .addTo(this.queue_view);
                }
                if (queue.filler.show_line) {
                    arrow(this.tool.reference.value(), TileCoordinates.move(this.tool.reference.value(), OffsetSelection.activeOffset(sel)), {
                        type: "none"
                    })
                        .setStyle({ color: "#00ffd9", weight: 1 })
                        .addTo(this.queue_view);
                }
            });
            if (!queue.filler.show_line) {
                leaflet.polyline(queue.queue.map(s => LeafletUtils.latLongFromVector2(Vector2.add(this.tool.reference.value(), OffsetSelection.activeOffset(s)))))
                    .setStyle({ color: "#ffff00", weight: 3 })
                    .addTo(this.queue_view);
            }
        }
    }
    CalibrationTool.Layer = Layer;
    class HoverTileDisplay extends GameMapControl {
        constructor(tool) {
            super({
                type: "gapless",
                position: "top-left"
            }, c().css("padding", "2px"));
            this.tool = tool;
        }
        eventHover(event) {
            function getcamerapos(coord) {
                return `${coord.level},${~~(coord.x / 64)},${~~(coord.y / 64)},${coord.x % 64},${coord.y % 64}`;
            }
            event.onPre(() => {
                this.content.text(`${getcamerapos(event.tile())}: ${Vector2.toString(Vector2.sub(event.tile(), this.tool.reference.value()))}`);
                this.tool.updateHoveredTileView(event.tile());
            });
        }
    }
    CalibrationTool.HoverTileDisplay = HoverTileDisplay;
})(CalibrationTool || (CalibrationTool = {}));
